import React from 'react';
import { Col, Form, FormModal, InputNumber, Row } from 'components';
import { PercentageIcon } from 'components/common/icons';
import { preventNonNumericValue } from 'utils/helpers';
import { usePricingRulesEditModal } from 'feature/clientTabs/payments/pricingRules/edit/usePricingRulesEditModal';

const { Item } = Form;

export interface IPricingRulesEditModal {
  modalState: boolean;
  onModalStateChange: () => void;
  dataToEdit?: Record<string, any>;
}

export type ClientPricingRuleType = {
  id: string;
  markup: number;
  minMargin: number;
  maxMargin: number;
  minLeadTime: number;
  maxLeadTime: number;
  discount: number;
  type: string;
};

export const PricingRulesEditModal = (props: IPricingRulesEditModal) => {
  const { modalState, onModalStateChange, dataToEdit } = props;
  const { onFinish, onCancel, form, isLoading, clearFieldValidation } = usePricingRulesEditModal({
    modalState,
    onModalStateChange,
    dataToEdit,
  });
  return (
    <FormModal
      form={form}
      title={'Update Pricing Rules'}
      width={680}
      okText={'Save'}
      open={modalState}
      isLoading={isLoading}
      onCancel={onCancel}
      onSubmit={onFinish}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Item
            label={'Margin'}
            name={'margin'}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);
                  if (isNaN(num)) {
                    return Promise.reject(new Error('Margin must be a valid number.'));
                  }
                  if (num < 0) {
                    return Promise.reject(new Error('Margin must be a positive number.'));
                  }
                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Margin can have up to 2 decimal points.'));
                  }
                  const minMargin = getFieldValue('minMargin');
                  const maxMargin = getFieldValue('maxMargin');

                  if (minMargin !== undefined && num < minMargin) {
                    return Promise.reject(new Error(`Margin must be greater than or equal to Min margin.`));
                  }
                  if (maxMargin !== undefined && num > maxMargin) {
                    return Promise.reject(new Error(`Margin must be less than or equal to Max margin.`));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
            />
          </Item>
        </Col>
        <Col span={6}>
          <Item
            label={'Margin Range'}
            name={'minMargin'}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);
                  if (isNaN(num)) {
                    return Promise.reject(new Error('Min margin must be a valid number.'));
                  }
                  if (num < 0) {
                    return Promise.reject(new Error('Min margin must be a positive number.'));
                  }
                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Min margin can have up to 2 decimal points.'));
                  }
                  const maxMargin = getFieldValue('maxMargin');
                  if (maxMargin !== undefined && maxMargin !== null && num > maxMargin) {
                    return Promise.reject(new Error('Min margin cannot be greater than Max margin.'));
                  }
                  const margin = getFieldValue('margin');
                  if (margin !== undefined && margin !== null && margin >= num) {
                    clearFieldValidation(form, 'margin');
                  }
                  clearFieldValidation(form, 'maxMargin');
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
              placeholder={'Min'}
            />
          </Item>
        </Col>
        <Col span={6}>
          <Item
            label={<span style={{ display: 'none' }}></span>}
            name={'maxMargin'}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);
                  if (isNaN(num)) {
                    return Promise.reject(new Error('Max margin must be a valid number.'));
                  }
                  if (num < 0) {
                    return Promise.reject(new Error('Max margin must be a positive number.'));
                  }
                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Max margin can have up to 2 decimal points.'));
                  }

                  const minMargin = getFieldValue('minMargin');
                  if (minMargin !== undefined && num < minMargin) {
                    return Promise.reject(new Error('Max margin cannot be less than Min margin.'));
                  }
                  clearFieldValidation(form, 'minMargin');
                  const margin = getFieldValue('margin');
                  if (margin !== undefined && margin !== null && margin <= num) {
                    clearFieldValidation(form, 'margin');
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
              placeholder={'Max'}
            />
          </Item>
        </Col>
        <Col span={12}>
          <Item
            name={'discount'}
            label={'Discount'}
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);

                  if (isNaN(num)) {
                    return Promise.reject(new Error('Discount must be a valid number.'));
                  }

                  if (num < 0) {
                    return Promise.reject(new Error('Discount must be a positive number.'));
                  }

                  if (num >= 100) {
                    return Promise.reject(new Error('Discount must be less than 100.'));
                  }

                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Discount can have up to 2 decimal points.'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
            />
          </Item>
        </Col>
      </Row>
    </FormModal>
  );
};
